<template>
  <BlocksHeroSection>Teams</BlocksHeroSection>
  <ContainerBasic>
    <TableSortable
      :default-columns="defaultColumns"
      :selected-columns="selectedColumns"
      :columns="columns"
      :loading="pending"
      :total="total"
      :per-page="perPage"
      :page-count="pageCount"
      :dictionary="localDictionary"
      plural="Teams"
      v-model:year="year"
      v-model:page="page"
      v-model:sort="sort"
      v-model:filters-shown="filtersShown"
      :min-year="2024"
      @update:sort="refresh"
      :rows="teams ?? []"
      @selected-columns-update="onSelectedColumnsChange"
      @reset-filters="resetRefs"
    >
      <template #filters>
        <FiltersSport v-model="sport" />
        <FormKit type="text" label="Team Name" v-model="searchTermTeamName" />
        <FiltersDivision v-model="division" />
        <FiltersConference v-model="conference" />
      </template>
      <template #index-data="{ row }">
        {{ row.resultIndex }}
      </template>
      <template #team_name-data="{ row }">
        <NuxtLink class="text-left block" :to="{ path: `/teams/${row.team_id}` }">
          <TeamLogo :team="row" size="tiny" />
          <span class="underline">{{ row.team_name }}</span>
        </NuxtLink>
      </template>
      <template #conference_abbreviation-data="{ row }">
        <NuxtLink
          class="underline block text-left"
          :to="{
            path: `/teams/${row.team_id}`,
            query: { conference_id: row.conference_id },
          }"
        >
          {{ row.conference_abbreviation }}
        </NuxtLink>
      </template>
      <template #current_rpi-data="{ row }">
        {{ row.current_rpi || '--' }}
      </template>
      <template #total_wins-data="{ row }">
        {{ sumBaseballRecords(row) }}
      </template>
      <template #integer_64_rank_total-data="{ row }">
        <template v-if="hasAccess">
          {{ row.integer_64_rank_total || '--' }}
        </template>
        <template v-else>
          <i class="fas fa-lock text-cardinal"></i>
        </template>
      </template>
      <template #rank_weighted_run_created_per_35_at_bat-data="{ row }">
        <template v-if="hasAccess">
          {{ row.rank_weighted_run_created_per_35_at_bat || '--' }}
        </template>
        <template v-else>
          <i class="fas fa-lock text-cardinal"></i>
        </template>
      </template>
      <template #rank_weighted_run_allowed_per_35_at_bat-data="{ row }">
        <template v-if="hasAccess">
          {{ row.rank_weighted_run_allowed_per_35_at_bat || '--' }}
        </template>
        <template v-else>
          <i class="fas fa-lock text-cardinal"></i>
        </template>
      </template>
      <template #sixty_four_rank_weighted_run_efficiency-data="{ row }">
        <template v-if="hasAccess">
          {{ row.sixty_four_rank_weighted_run_efficiency || '--' }}
        </template>
        <template v-else>
          <i class="fas fa-lock text-cardinal"></i>
        </template>
      </template>
    </TableSortable>
  </ContainerBasic>
</template>

<script setup lang="ts">
useHead({
  title: 'Explore NCAA Teams - Comprehensive Team Analytics',
  meta: [
    {
      name: 'description',
      content:
        'Dive into detailed analytics of NCAA baseball teams. Compare team performances, stats, and rankings to find your perfect match.',
    },
  ],
});

import { TeamsService } from '~/lib/services/teams.service';
import type { Database } from '~/types';
import type { Tables, Views } from '~/types/database-helpers';
import type { Column } from '~/types/data-dictionary';
import { useTeamsStore } from '~/stores/teams.store';
import { getQueryPagination } from '~/types/database-query.d';
import { defaultIndexCol } from '~/lib/table-columns/defaults';
const nuxtApp = useNuxtApp();
const supabase = useSupabaseClient<Database>();
const teamsService = new TeamsService(supabase);

const page = ref(1);
const year = ref(2026);
const filtersShown = ref(false);
const team = ref();
const perPage = ref(50);
const searchTermTeamName = ref('');
const division = ref('D-I');
const conference = ref<number | null>(null);
const sport = ref<Sport>('Baseball');
const store = useProfileStore();
const { hasAccess, hasCurrentBaseballPortalPass, hasCurrentSoftballPortalPass } =
  storeToRefs(store);
const teamsStore = useTeamsStore();
const { total, pageCount } = storeToRefs(teamsStore);
const { setTotal, setPageCount } = teamsStore;

useInitialSportSelection(sport, hasCurrentBaseballPortalPass, hasCurrentSoftballPortalPass);

let query = ref('');
const { fillQueryParamValuesFromRefs, stopWatchers } = useSyncWithQuery<
  string | number | undefined | null
>([
  { key: 'page', ref: page, type: 'number' },
  { key: 'conference', ref: conference, type: 'number' },
  { key: 'searchTermTeamName', ref: searchTermTeamName },
  { key: 'division', ref: division },
  { key: 'sport', ref: sport },
]);

const DEFAULT_SORT_FIELD = computed(() =>
  hasAccess.value ? 'integer_64_rank_total' : 'current_rpi'
);

const sort = ref<{ column: string; direction: 'asc' | 'desc' }>({
  column: hasAccess.value ? 'integer_64_rank_total' : 'current_rpi',
  direction: 'asc',
});

let filteredPlayers = computed(() =>
  query.value === ''
    ? teams
    : teams.filter((team) =>
        team.name
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, ''))
      )
);

const TABLE_NAMES = ['team_rank'];
const { parseData, localDictionary } = useDataDictionary(TABLE_NAMES);

const defaultColumns = transformColumns(
  [
    {
      key: 'integer_64_rank_total',
      label: '64 Ranking',
      sortable: false,
      direction: 'asc',
    },
    { key: 'team_name', label: 'Name', sortable: true, class: '!text-left' },
    {
      key: 'conference_abbreviation',
      label: 'Conference',
      sortable: true,
      class: '!text-left',
    },
    {
      key: 'rank_weighted_run_created_per_35_at_bat',
      label: 'wRCE35',
      sortable: false,
      direction: 'desc',
    },
    {
      key: 'rank_weighted_run_allowed_per_35_at_bat',
      label: 'wRAE35',
      sortable: false,
      direction: 'desc',
    },
    { key: 'current_rpi', label: 'RPI', sortable: true },
    {
      key: 'total_wins',
      label: 'Overall Record',
      sortable: true,
      direction: 'desc',
    },
    {
      key: 'sixty_four_rank_weighted_run_efficiency',
      label: '64 Ranking',
      sortable: false,
      direction: 'desc',
    },
  ],
  localDictionary.value
);
const selectedColumns = ref(defaultColumns);
const columns = computed(() => {
  const showIndex = sort.value.column != DEFAULT_SORT_FIELD.value;
  let filteredDefaultCols = defaultColumns.filter((column) => {
    return selectedColumns.value.find((col) => col.key === column.key);
  });

  if (hasAccess.value) {
    filteredDefaultCols = filteredDefaultCols.map((col) => ({
      ...col,
      sortable: true,
    }));
  }

  if (showIndex) {
    filteredDefaultCols.unshift(defaultIndexCol);
    // remove defaultSortCol from the list of columns
    // filteredDefaultCols = filteredDefaultCols.filter((col) => col.key !== DEFAULT_SORT_FIELD.value);
  }
  return filteredDefaultCols;
});

const onSelectedColumnsChange = ($event: Column[]) => {
  selectedColumns.value = $event;
};

const colDirection = computed(() => {
  const colDirection: 'desc' | 'asc' =
    defaultColumns.find((col) => col.key == sort.value.column)?.direction === 'desc'
      ? 'desc'
      : 'asc';
  return colDirection;
});

const startingIndex = computed(() => {
  return colDirection.value == sort.value.direction
    ? (page.value - 1) * perPage.value
    : total.value - (page.value - 1) * perPage.value;
});

const getTeams = async (): Promise<Views<'teams_list'>[]> => {
  let { data, error, count } = await (() => {
    const pagination = getQueryPagination(page.value, perPage.value);
    console.log('getTeams');

    let r = supabase
      .from('teams_list')
      .select(`*`, { count: 'exact' })
      .range(pagination.startIndex, pagination.endIndex)
      .limit(perPage.value)
      .order(sort.value.column, { ascending: sort.value.direction == 'asc' });

    // if (position.value) {
    //   r = r.eq('position', position.value);
    // }
    // if (classification.value) {
    //   r = r.eq('classification', classification.value);
    // }
    if (searchTermTeamName.value && searchTermTeamName.value.length >= 3) {
      r = r.ilike('team_name', `%${searchTermTeamName.value}%`);
    }
    if (division.value) {
      r = r.eq('division', division.value);
    }
    if (conference.value) {
      r = r.eq('conference_id', conference.value);
    }
    // if (team.value) {
    //   r = r.eq('team_id', team.value.id);
    // }
    if (year.value) {
      r = r.eq('schedule_year', year.value);
    }
    // if (sport.value) {
    //   r = r.eq('sport', sport.value);
    // }
    r = r.eq('sport', sport.value);
    return r;
  })();

  if (error) {
    return { teams: [], count: 0, error };
  }

  if (count) {
    setTotal(count);
    setPageCount(count / perPage.value);
  } else {
    setTotal(0);
    setPageCount(0);
    console.log('no count');
  }

  let formattedData = parseData<Views<'teams_list'>>(data);

  formattedData = formattedData.map((row) => {
    return {
      ...row,
      resultIndex:
        colDirection.value === sort.value.direction
          ? startingIndex.value + formattedData.indexOf(row) + 1
          : startingIndex.value - formattedData.indexOf(row),
    };
  });

  return formattedData;
};

const filtersToWatch = [searchTermTeamName, division, conference, year, sport];
const {
  data: teams,
  pending,
  refresh,
} = await useAsyncData<Views<'teams_list'>[]>('teams', getTeams, {
  watch: filtersToWatch,
});
watch([page], () => {
  console.log('page changed, refreshing data');
  refresh();
});
watch(filtersToWatch, () => {
  console.log('filter changed, resetting page');
  page.value = 1;
});
const resetRefs = () => {
  console.log('resetting refs');
  searchTermTeamName.value = '';
  conference.value = null;
  division.value = 'D-I';
  page.value = 1;
  sort.value = {
    column: hasAccess.value ? 'integer_64_rank_total' : 'current_rpi',
    direction: 'asc',
  };
};
onBeforeRouteLeave(() => {
  stopWatchers();
});
onMounted(() => {
  fillQueryParamValuesFromRefs();
});
</script>
